import { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Check, Phone, Play } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";
import { products } from "@/data/products";
import Seo, { SITE_URL } from "@/components/Seo";
import WoodSlideshow from "@/components/WoodSlideshow";

export default function ProductPage() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const product = products.find((p) => p.id === Number(id));
  const [active, setActive] = useState(0);
  const [showVideo, setShowVideo] = useState(true);

  const gallery = useMemo(() => {
    if (!product) return [];
    const others = products.filter((p) => p.cat === product.cat && p.id !== product.id).map((p) => p.img);
    return [product.img, ...others.slice(0, 3)];
  }, [product]);

  if (!product) return <Navigate to="/" replace />;

  const name = lang === "id" ? product.nameId : product.nameEn;
  const desc = lang === "id" ? product.descId : product.descEn;
  const related = products.filter((p) => p.cat === product.cat && p.id !== product.id).slice(0, 3);
  const L = t.productPage;

  const rows = [
    { label: L.size, value: product.sizes.join(" · "), testid: "spec-sizes" },
    { label: L.grade, value: product.grade, testid: "spec-grade" },
    { label: L.mc, value: product.mc, testid: "spec-mc" },
    { label: L.minOrder, value: product.minOrder, testid: "spec-minorder" },
    { label: L.stock, value: L.stockValue, testid: "spec-stock" },
  ];

  return (
    <main className="pt-24 sm:pt-28 pb-24 bg-[#0B1220] blueprint-grid min-h-screen" data-testid="product-page">
      <Seo
        title={`${product.nameId} — ${product.dim} | CV. Sinar Al Mubarokah`}
        description={`${product.nameId} ukuran ${product.dim}. ${product.descId.slice(0, 120)} Order via WA 0822-8280-0020.`}
        path={`/produk/${product.id}`}
        image={product.img}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.nameId,
          description: product.descId,
          image: product.img.startsWith("http") ? product.img : SITE_URL + product.img,
          brand: { "@type": "Organization", name: "CV. Sinar Al Mubarokah" },
        }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/" state={{ scrollTo: "katalog" }} data-testid="back-to-catalog"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-sky-400 transition-colors">
          <ArrowLeft size={16} /> {L.back}
        </Link>

        <div className="mt-8 grid lg:grid-cols-2 gap-10 lg:gap-14">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {product.video && showVideo ? (
              <div className="clip-industrial overflow-hidden border-4 border-slate-700/60 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.7)] bg-[#070E1A] max-w-[360px] mx-auto">
                <iframe
                  src={`${product.video}?rel=0`}
                  title={`${name} — video`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full aspect-[9/16]"
                  data-testid="product-video"
                />
              </div>
            ) : (
              <div className="clip-industrial overflow-hidden border-4 border-slate-700/60 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.7)] bg-[#101B2E]">
                <img src={gallery[active]} alt={`${name} — ${product.dim}`} data-testid="product-main-image"
                  className="w-full aspect-[4/3] object-cover" />
              </div>
            )}
            <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-sky-400 font-semibold">{L.gallery}</p>
            <div className="mt-3 grid grid-cols-4 gap-3" data-testid="product-gallery">
              {product.video && (
                <button onClick={() => setShowVideo(true)} data-testid="gallery-video-thumb"
                  className={`relative rounded-xl overflow-hidden border-2 transition-all ${showVideo ? "border-sky-500 scale-[1.03]" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={product.img} alt={`${name} video`} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-slate-950/40">
                    <Play size={22} className="text-white fill-white" />
                  </span>
                </button>
              )}
              {gallery.map((g, i) => (
                <button key={i} onClick={() => { setActive(i); setShowVideo(false); }} data-testid={`gallery-thumb-${i}`}
                  className={`rounded-xl overflow-hidden border-2 transition-all ${!showVideo && active === i ? "border-sky-500 scale-[1.03]" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={g} alt={`${name} foto ${i + 1}`} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <span className="inline-block bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full">
              {t.catalog.ready}
            </span>
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="product-title">
              {name}
            </h1>
            <p className="mt-3 font-mono text-sm text-sky-400 font-semibold tracking-wide">{product.dim}</p>

            <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed">{desc}</p>

            <div className="mt-8 bg-[#101B2E] rounded-2xl border border-slate-800 overflow-hidden" data-testid="spec-table">
              <p className="px-6 py-4 bg-[#070E1A] text-white font-display font-bold text-sm tracking-wide border-b border-slate-800">{L.spec}</p>
              <dl className="divide-y divide-slate-800">
                {rows.map((r) => (
                  <div key={r.testid} className="px-6 py-4 flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-6">
                    <dt className="text-sm text-slate-400 shrink-0">{r.label}</dt>
                    <dd className="text-sm font-semibold text-slate-100 sm:text-right" data-testid={r.testid}>{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <ul className="mt-6 space-y-2.5">
              {product.uses.map((u) => (
                <li key={u} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check size={16} className="text-sky-400 shrink-0 mt-0.5" /> {u}
                </li>
              ))}
            </ul>

            <div className="mt-8 bg-[#101B2E] rounded-2xl border border-slate-800 p-6 sm:p-8" data-testid="order-guide">
              <h2 className="font-display font-bold text-lg text-white">{L.guideTitle}</h2>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{L.guideIntro}</p>
              <ol className="mt-4 space-y-3">
                {L.guideSteps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-sky-500/15 border border-sky-500/40 text-sky-300 font-display font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed border-t border-slate-800 pt-4">{L.guideNote}</p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={waLink(t.catalog.orderMsg(name, product.dim))} target="_blank" rel="noopener noreferrer"
                data-testid="product-wa-order"
                className="inline-flex flex-1 items-center justify-center gap-2.5 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-6 py-4 rounded-2xl transition-colors">
                <MessageCircle size={19} /> {L.order}
              </a>
              <a href="tel:+6282282800020" data-testid="product-call-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-slate-100 font-semibold px-6 py-4 rounded-2xl hover:border-sky-500 hover:text-sky-400 transition-colors">
                <Phone size={18} /> 0822-8280-0020
              </a>
            </div>
          </motion.div>
        </div>

        {product.id === 1 && <WoodSlideshow />}

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display font-extrabold tracking-tight text-white text-xl sm:text-2xl" data-testid="related-title">{L.related}</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/produk/${p.id}`} data-testid={`related-product-${p.id}`}
                  className="group bg-[#101B2E] rounded-2xl border border-slate-800 overflow-hidden hover:border-sky-500/60 hover:shadow-[0_24px_50px_-20px_rgba(14,116,144,0.45)] transition-all">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.img} alt={`${lang === "id" ? p.nameId : p.nameEn}`} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <p className="font-display font-bold text-slate-100">{lang === "id" ? p.nameId : p.nameEn}</p>
                    <p className="mt-1 text-sm text-slate-400">{p.dim}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
